import { action, computed, makeObservable, observable } from "mobx";
import { HospitalHubStore } from "@hospital/stores/Hubs/HospitalHub";
import { DateTime } from "luxon";
import { IDisposable } from "@common/types";
import { HospitalAppStore } from "../rootStores/HospitalAppStore";
import { AppointmentStatus } from "@common/models/Enums";

/**
 * This class subscribes to things appointment related on the HospitalHub and updates some observables when things happen
 * Currently this is just increasing a counter and the change in that counter is being used to trigger an observer useEffect
 * that in turn refreshes the calendar/flow page.
 * This is actually even more than it was previously, as this will refresh the calendar if the schedule changes, templates are added
 * overrides are created, adhoc vets are scheduled, appointments are rescheduled, online bookings happen
 * or if an appointment was updated or it's status changed.
 */
export class AppointmentSubscriberStore implements IDisposable {
    messageHub: HospitalHubStore;
    hospitalApp: HospitalAppStore;
    @observable private _isCalendarDirty: boolean = false;

    constructor(hospitalApp: HospitalAppStore) {
        this.messageHub = HospitalHubStore.currentInstance;
        this.hospitalApp = hospitalApp;
        makeObservable(this, undefined, { autoBind: true })
        this.subscribeToHubEvents();
    }

    @computed get IsCalendarDirty() {
        return this._isCalendarDirty;
    }

    @computed get LoadedDate() {
        return this.hospitalApp.calendarLoadedDate;
    }

    private subscribeToHubEvents(unsubscribe: boolean = false) {
        const method = !unsubscribe ? this.messageHub.registerMethod : this.messageHub.unregisterMethod;
        method({ eventName: "onAppointmentUpdated", callback: this.onAppointmentUpdated });
        method({ eventName: "onAppointmentStatusChanged", callback: this.onAppointmentStatusChanged });
        method({ eventName: "onAppointmentRescheduled", callback: this.onAppointmentRescheduled });
        method({ eventName: "onAppointmentReopened", callback: this.onAppointmentReopened });
        method({ eventName: "onUpdatedHospitalSchedule", callback: this.onUpdatedHospitalSchedule });
        method({ eventName: "onUpdatedHospitalScheduleAdHoc", callback: this.onUpdatedHospitalScheduleAdHoc });
        method({ eventName: "onUpdatedHospitalScheduleOverrides", callback: this.onUpdatedHospitalScheduleOverrides });
        method({ eventName: "onUpdatedHospitalScheduleTemplate", callback: this.onUpdatedHospitalScheduleTemplate });
        method({ eventName: "onAppointmentBookedOnline", callback: this.onAppointmentBookedOnline });
    }

    dispose() {
        this.subscribeToHubEvents(true);
    }

    @action.bound setCalendarDirty(isDirty: boolean = true) {
        this._isCalendarDirty = isDirty;
    }

    @action.bound onUpdatedHospitalSchedule(hospitalId: number, strChangeDates: string[]) {
        if (this.isCurrentHospital(hospitalId) && _.some(strChangeDates, p => this.isDateLoadedDate(p)))
            this.setCalendarDirty();
    }

    @action.bound onUpdatedHospitalScheduleAdHoc(hospitalId: number, strScheduleDate: string | null) {
        if (this.isCurrentHospital(hospitalId) && this.isDateLoadedDate(strScheduleDate)) {
            this.setCalendarDirty();
        }
    }

    @action.bound onUpdatedHospitalScheduleTemplate(hospitalId: number, templateId: number, templateName: string) { //<- do not delete the unused parameters, the signalr hub will not execute this call back if parameters don't exist for it's server side parameters.
        if (this.isCurrentHospital(hospitalId))
            this.setCalendarDirty();
    }

    @action.bound onUpdatedHospitalScheduleOverrides(hospitalId: number, strDateScheduled: string) {
        if (this.isCurrentHospital(hospitalId) && this.isDateLoadedDate(strDateScheduled))
            this.setCalendarDirty();
    }

    @action.bound onAppointmentUpdated(hospitalId: number, reservationId: number | null, visitId: number | null, strStartDate: string) {
        this.processAppointmentChange(hospitalId, strStartDate);
    }

    @action.bound onAppointmentReopened(hospitalId: number, reservationId: number | null, visitId: number | null, strStartDate: string) {
        this.processAppointmentChange(hospitalId, strStartDate);
    }

    @action.bound onAppointmentRescheduled(hospitalId: number, reservationId: number | null, visitId: number | null, strStartDate: string) {
        this.processAppointmentChange(hospitalId, strStartDate);
    }

    @action.bound onAppointmentStatusChanged(hospitalId: number, reservationId: number | null, visitId: number | null, appointmentStatus: AppointmentStatus, strStartDate: string, strEndDate: string) {
        this.processAppointmentChange(hospitalId, strStartDate);
    }

    @action.bound onAppointmentBookedOnline(hospitalId: number, strStartDate: string | null) {
        this.processAppointmentChange(hospitalId, strStartDate);
    }

    //#region Helpers
    private isDateLoadedDate(strDate: string | null) {
        if (strDate === null)
            return false;
        var loadedDate = this.LoadedDate.toLocaleString(DateTime.DATE_SHORT);
        var date = DateTime.fromISO(strDate, { zone: 'UTC' }).toLocaleString(DateTime.DATE_SHORT, {});
        return loadedDate === date;
    }

    private isCurrentHospital(hospitalId: number) {
        if (this.hospitalApp && this.hospitalApp.currentHospital && this.hospitalApp.currentHospital.hospitalId === hospitalId)
            return true;
        return false;
    }

    @action.bound private processAppointmentChange(hospitalId: number, strStartDate: string | null) {
        if (this.isCurrentHospital(hospitalId) && this.isDateLoadedDate(strStartDate)) {
            this.setCalendarDirty();
        }
    }
    //#endregion
}
